import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormHelperText, IconButton, InputAdornment, InputLabel, ListItemText, MenuItem, OutlinedInput, Paper, Select, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import { authRequest } from "../services/auth";
import BulkUserDialog from "./BulkUserDialog";
import { ErrorState, LoadingState, RefreshButton } from "./AsyncViewState";

const GROUPS = ["SustainAdmin", "GlobalAnnouncer", "SuperAdmin", "Admin", "OpsTeam", "Developer", "General"];
const emptyForm = { userId: "", name: "", email: "", password: "", groups: ["General"], sendCredentials: false };

function generatePassword(length = 16) {
  const sets = ["ABCDEFGHJKLMNPQRSTUVWXYZ", "abcdefghijkmnopqrstuvwxyz", "23456789", "!@#$%^&*_-+"];
  const all = sets.join("");
  const values = new Uint32Array(length);
  window.crypto.getRandomValues(values);
  const password = sets.map((set, index) => set[values[index] % set.length]);
  for (let index = sets.length; index < length; index += 1) password.push(all[values[index] % all.length]);
  for (let index = password.length - 1; index > 0; index -= 1) {
    const swap = values[index] % (index + 1);
    [password[index], password[swap]] = [password[swap], password[index]];
  }
  return password.join("");
}

export default function SiteConfigUsers({ isSuperAdmin = false, isSustainAdmin = false }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [originalEmail, setOriginalEmail] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetSendCredentials, setResetSendCredentials] = useState(false);
  const [resetSaving, setResetSaving] = useState(false);

  const validCredentialEmail = /^[^\s@]+@cognizant\.com$/i.test(form.email.trim());

  const loadUsers = useCallback(async () => {
    setLoading(true); setError("");
    try { const { data } = await authRequest().get("/users"); setUsers(data); }
    catch (err) { setError(err.response?.data?.error || "Unable to load users"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const openCreate = () => { setForm({ ...emptyForm, groups: [...emptyForm.groups] }); setOriginalEmail(""); setDialog("create"); setError(""); setSuccess(""); };
  const openEdit = (user) => { setForm({ userId: user.userId, name: user.name, email: user.email, password: "", groups: [...user.groups], sendCredentials: false }); setOriginalEmail(user.email || ""); setDialog("edit"); setError(""); setSuccess(""); };

  const copyPassword = async () => {
    if (!form.password) return;
    await navigator.clipboard.writeText(form.password);
    setSuccess("Password copied to clipboard");
  };

  const save = async (event) => {
    event.preventDefault(); setSaving(true); setError(""); setSuccess("");
    if (form.email.trim() && !/^[^\s@]+@cognizant\.com$/i.test(form.email.trim())) {
      setError("Email must end with @cognizant.com"); setSaving(false); return;
    }
    try {
      let response;
      if (dialog === "create") response = await authRequest().post("/users", form);
      else response = await authRequest().put(`/users/${encodeURIComponent(form.userId)}`, { name: form.name, email: form.email, groups: form.groups });
      setDialog(null);
      const emailWarning = response.data?.emailWarning;
      setSuccess(dialog === "create"
        ? `User created successfully${response.data?.credentialsEmailSent ? " and credentials emailed" : ""}`
        : "User updated successfully");
      await loadUsers();
      if (emailWarning) setError(emailWarning);
    } catch (err) { setError(err.response?.data?.error || "Unable to save user"); }
    finally { setSaving(false); }
  };

  const remove = async (user) => {
    if (!window.confirm(`Delete ${user.name} (${user.userId})? This cannot be undone.`)) return;
    setError(""); setSuccess("");
    try { await authRequest().delete(`/users/${encodeURIComponent(user.userId)}`); setSuccess("User deleted"); await loadUsers(); }
    catch (err) { setError(err.response?.data?.error || "Unable to delete user"); }
  };

  const openPasswordReset = (user) => {
    setResetUser(user); setResetPassword(generatePassword());
    setResetSendCredentials(/^[^\s@]+@cognizant\.com$/i.test(String(user.email || "").trim()));
    setError(""); setSuccess("");
  };

  const resetUserPassword = async (event) => {
    event.preventDefault(); setResetSaving(true); setError(""); setSuccess("");
    try {
      const { data } = await authRequest().post(`/users/${encodeURIComponent(resetUser.userId)}/reset-password`, { password: resetPassword, sendCredentials: resetSendCredentials });
      setResetUser(null); setResetPassword(""); setResetSendCredentials(false);
      setSuccess(`${data.message || "Password reset successfully"}${data.credentialsEmailSent ? " and credentials emailed" : ""}`);
      if (data.emailWarning) setError(data.emailWarning);
    } catch (err) { setError(err.response?.data?.error || "Unable to reset password"); }
    finally { setResetSaving(false); }
  };

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, pb: 5 }}>
      <Box sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", gap: 2, mb: 3, flexDirection: { xs: "column", sm: "row" } }}>
        <Box><Typography variant="h4" sx={{ fontWeight: 750, color: "text.primary" }}>User management</Typography><Typography color="text.secondary" sx={{ mt: .5 }}>Create accounts and assign one or more roles.</Typography></Box>
        <Stack direction="row" spacing={1}><RefreshButton onClick={loadUsers} loading={loading} /><Button variant="outlined" startIcon={<UploadFileRoundedIcon />} onClick={() => setBulkOpen(true)} sx={{ textTransform: "none", borderRadius: 2, px: 2 }}>Bulk add</Button><Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate} sx={{ textTransform: "none", borderRadius: 2, px: 2.5 }}>Add user</Button></Stack>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        {loading ? <LoadingState label="Loading users…" minHeight={260} /> : error && users.length === 0 ? <ErrorState onRetry={loadUsers} message={error} /> : users.length === 0 ?
          <Box sx={{ py: 9, textAlign: "center", color: "text.secondary" }}><PeopleAltOutlinedIcon sx={{ fontSize: 48, opacity: .4 }} /><Typography sx={{ mt: 1 }}>No users found</Typography></Box> :
          <TableContainer><Table><TableHead><TableRow><TableCell>Employee ID</TableCell><TableCell>Name</TableCell><TableCell>Email</TableCell><TableCell>Roles</TableCell><TableCell>Status</TableCell>{isSuperAdmin && <TableCell align="right">Actions</TableCell>}</TableRow></TableHead>
            <TableBody>{users.map((user) => <TableRow key={user.userId} hover><TableCell sx={{ fontWeight: 700 }}>{user.userId}</TableCell><TableCell>{user.name}</TableCell><TableCell>{user.email}</TableCell><TableCell><Stack direction="row" gap={.75} flexWrap="wrap">{user.groups.map((group) => <Chip key={group} label={group === "OpsTeam" ? "Ops" : group} size="small" color={group === "SuperAdmin" ? "primary" : "default"} />)}</Stack></TableCell><TableCell><Chip label={user.enabled ? "Active" : "Disabled"} size="small" color={user.enabled ? "success" : "default"} variant="outlined" /></TableCell>{isSuperAdmin && <TableCell align="right">{isSustainAdmin && <Tooltip title="Reset password"><IconButton aria-label={`Reset password for ${user.name}`} color="warning" onClick={() => openPasswordReset(user)}><LockResetRoundedIcon /></IconButton></Tooltip>}<IconButton aria-label={`Edit ${user.name}`} onClick={() => openEdit(user)}><EditOutlinedIcon /></IconButton><IconButton aria-label={`Delete ${user.name}`} color="error" onClick={() => remove(user)}><DeleteOutlineRoundedIcon /></IconButton></TableCell>}</TableRow>)}</TableBody>
          </Table></TableContainer>}
      </Paper>

      <Dialog open={Boolean(dialog)} onClose={() => !saving && setDialog(null)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={save}>
          <DialogTitle sx={{ fontWeight: 750 }}>{dialog === "create" ? "Add a new user" : "Edit user"}</DialogTitle>
          <DialogContent><Stack spacing={2.25} sx={{ pt: 1 }}>
            <TextField label="Employee ID" value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })} required disabled={dialog === "edit"} />
            <TextField label="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <TextField
              label={originalEmail ? "Email" : "Email (optional)"}
              type="email"
              value={form.email}
              onChange={(e) => {
                const nextEmail = e.target.value;
                const wasValid = /^[^\s@]+@cognizant\.com$/i.test(form.email.trim());
                const isValid = /^[^\s@]+@cognizant\.com$/i.test(nextEmail.trim());
                setForm({ ...form, email: nextEmail, sendCredentials: isValid ? (wasValid ? form.sendCredentials : true) : false });
              }}
              required={Boolean(originalEmail)}
              helperText={originalEmail ? "You can replace this address, but Cognito cannot remove it once set. Use @cognizant.com." : "If provided, must end with @cognizant.com."}
            />
            {dialog === "create" && <Box>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "flex-start" }}>
                <TextField
                  label="Initial password"
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  fullWidth
                  autoComplete="new-password"
                  InputProps={{ endAdornment: <InputAdornment position="end"><Tooltip title="Copy password"><span><IconButton onClick={copyPassword} disabled={!form.password} aria-label="Copy password"><ContentCopyRoundedIcon fontSize="small" /></IconButton></span></Tooltip></InputAdornment> }}
                />
                <Button variant="outlined" startIcon={<AutoAwesomeRoundedIcon />} onClick={() => setForm({ ...form, password: generatePassword() })} sx={{ minWidth: 190, minHeight: 56, textTransform: "none" }}>Auto-generate</Button>
              </Stack>
              <FormHelperText sx={{ display: "flex", alignItems: "center", gap: .6, mt: .8 }}>
                <Tooltip title="Cognito requires at least 12 characters, including uppercase, lowercase, a number, and a symbol."><InfoOutlinedIcon sx={{ fontSize: 16, cursor: "help" }} /></Tooltip>
                12+ characters with uppercase, lowercase, number, and symbol.
              </FormHelperText>
              <FormControlLabel
                sx={{ mt: 1.25, ml: 0, gap: .5 }}
                control={<Switch checked={form.sendCredentials} disabled={!validCredentialEmail} onChange={(event) => setForm({ ...form, sendCredentials: event.target.checked })} color="primary" />}
                label={<Stack direction="row" spacing={.75} alignItems="center"><EmailOutlinedIcon fontSize="small" /><Typography variant="body2" fontWeight={600}>Email Credentials</Typography></Stack>}
              />
              <FormHelperText>{validCredentialEmail ? (form.sendCredentials ? "The initial password will be emailed when this user is created." : "Email Credentials is disabled.") : "Enter a valid @cognizant.com email to enable credential email."}</FormHelperText>
            </Box>}
            {isSuperAdmin ? <FormControl required>
              <InputLabel id="user-roles-label">Roles</InputLabel>
              <Select
                labelId="user-roles-label"
                multiple
                value={form.groups}
                onChange={(event) => {
                  const value = event.target.value;
                  setForm((current) => ({ ...current, groups: typeof value === "string" ? value.split(",") : value }));
                }}
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) => <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>{selected.map((role) => <Chip key={role} label={role} size="small" />)}</Box>}
              >
                {GROUPS.map((role) => <MenuItem key={role} value={role}><Checkbox checked={form.groups.includes(role)} /><ListItemText primary={role} /></MenuItem>)}
              </Select>
              <FormHelperText>Select one or more roles. Users can have multiple roles.</FormHelperText>
            </FormControl> : <Alert severity="info">New users created by Admin or Ops are assigned the General role.</Alert>}
          </Stack></DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}><Button onClick={() => setDialog(null)} disabled={saving}>Cancel</Button><Button type="submit" variant="contained" disabled={saving || form.groups.length === 0}>{saving ? <CircularProgress size={22} /> : dialog === "create" ? "Create user" : "Save changes"}</Button></DialogActions>
        </Box>
      </Dialog>
      <Dialog open={Boolean(resetUser)} onClose={() => !resetSaving && setResetUser(null)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={resetUserPassword}>
          <DialogTitle sx={{ fontWeight: 750 }}>Reset user password</DialogTitle>
          <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">This immediately replaces the password for {resetUser?.name} ({resetUser?.userId}).</Alert>
            <TextField
              label="New password"
              type="text"
              value={resetPassword}
              onChange={(event) => setResetPassword(event.target.value)}
              required
              autoComplete="new-password"
              InputProps={{ endAdornment: <InputAdornment position="end"><Tooltip title="Copy password"><span><IconButton onClick={() => navigator.clipboard.writeText(resetPassword)} disabled={!resetPassword} aria-label="Copy reset password"><ContentCopyRoundedIcon fontSize="small" /></IconButton></span></Tooltip></InputAdornment> }}
            />
            <Button variant="outlined" startIcon={<AutoAwesomeRoundedIcon />} onClick={() => setResetPassword(generatePassword())} sx={{ alignSelf: "flex-start", textTransform: "none" }}>Generate another password</Button>
            <FormHelperText>Use at least 12 characters with uppercase, lowercase, a number, and a symbol.</FormHelperText>
            <Box>
              <FormControlLabel
                sx={{ ml: 0, gap: .5 }}
                control={<Switch checked={resetSendCredentials} disabled={!/^[^\s@]+@cognizant\.com$/i.test(String(resetUser?.email || "").trim())} onChange={(event) => setResetSendCredentials(event.target.checked)} color="primary" />}
                label={<Stack direction="row" spacing={.75} alignItems="center"><EmailOutlinedIcon fontSize="small" /><Typography variant="body2" fontWeight={600}>Email new password</Typography></Stack>}
              />
              <FormHelperText>{/^[^\s@]+@cognizant\.com$/i.test(String(resetUser?.email || "").trim()) ? (resetSendCredentials ? `The new password will be emailed to ${resetUser?.email}.` : "Password email is disabled.") : "A valid @cognizant.com email is required to enable password email."}</FormHelperText>
            </Box>
          </Stack></DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}><Button onClick={() => setResetUser(null)} disabled={resetSaving}>Cancel</Button><Button type="submit" variant="contained" color="warning" disabled={resetSaving || !resetPassword}>{resetSaving ? <CircularProgress size={22} /> : "Reset password"}</Button></DialogActions>
        </Box>
      </Dialog>
      <BulkUserDialog open={bulkOpen} onClose={() => setBulkOpen(false)} onComplete={loadUsers} />
    </Box>
  );
}
